import { useTranslation } from 'react-i18next';
import type { CapiProductPage } from 'types/capi/pdp';

import ArrowIcon from '~/assets/icons/Arrow';
import ChevronIcon from '~/assets/icons/Chevron';
import SizeInfo from '~/components/SizeInfo';
import SizeSelectionEvent from '~/events/SizeSelectionEvent';
import usePageData from '~/hooks/usePageData';
import useRegion from '~/hooks/useRegion';
import useRootData from '~/hooks/useRootData';

import SizeSelectorGrid from './SizeSelectorGrid';

type Props = {
  forceExpandedSizeGrid?: boolean;
  isExpanded: boolean;
  selectedSize?: string;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedSize: React.Dispatch<React.SetStateAction<string | undefined>>;
  shouldShowSizeGrid: boolean;
  sizes: string[];
};

export default function SizeSelection({
  forceExpandedSizeGrid,
  selectedSize,
  shouldShowSizeGrid,
  setSelectedSize,
  setIsExpanded,
  isExpanded,
  sizes
}: Props) {
  const product = usePageData<CapiProductPage>();
  const { isBot } = useRootData();
  const { t } = useTranslation('pdp');
  const region = useRegion();

  const onGridToggleClick = () => {
    setIsExpanded(!isExpanded);
  };

  const onSizeSelect = (size: string) => {
    setSelectedSize(size);
    setIsExpanded(!size);
    new SizeSelectionEvent({ isBot, pageType: 'pdp' }).send();
  };

  const isExpandedOverride = Boolean(isExpanded || forceExpandedSizeGrid);

  if (shouldShowSizeGrid) {
    return (
      <>
        <div className="flex items-center justify-between">
          <h2 className="text-title-sm">{t('selectYourSize')}</h2>
          {!isExpandedOverride && <GridToggle onClick={onGridToggleClick} />}
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            {product.size_fit_guide && region.code === 'GB' && (
              <div
                className={`flex w-full flex-col items-center justify-center gap-4 ${product.allow_size_selection ? 'rounded-t-lg px-4 pt-4' : 'rounded-lg p-4'} ${product.size_fit_guide?.adjustment === 0 ? 'bg-grey-panel' : 'bg-yellow-label p-4'}`}
              >
                <SizeInfo sizeFitGuide={product.size_fit_guide} />
              </div>
            )}
            <div
              className={`flex flex-col gap-4 bg-grey-panel p-4 ${product.size_fit_guide ? 'rounded-b-lg' : 'rounded-lg'}`}
            >
              <SizeSelectorGrid
                onSizeSelect={onSizeSelect}
                selectedSize={selectedSize}
                isExpanded={isExpandedOverride}
                sizes={sizes}
              />
            </div>
          </div>
          {!selectedSize && (
            <div className="flex gap-2 bg-yellow-label p-2 text-sm tracking-wide">
              <span className="rotate-90">
                <ArrowIcon />
              </span>
              <span>{t('selectYourSizeTooltip')}</span>
            </div>
          )}
        </div>
      </>
    );
  }

  if (region.code === 'GB') return <SimplifiedSizeGuide product={product} />;
}

function SimplifiedSizeGuide({ product }: { product: CapiProductPage }) {
  if (!product.size_fit_guide) return null;

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-title-sm">Size guide</h2>
      <div
        className={`flex w-full flex-col items-center justify-center gap-4 rounded-lg p-4 ${product.size_fit_guide?.adjustment.toString() === '0' ? 'bg-grey-tile' : 'bg-yellow-label p-4'}`}
      >
        <SizeInfo sizeFitGuide={product.size_fit_guide} />
      </div>
    </div>
  );
}

function GridToggle({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('pdp');

  return (
    <button className="flex items-center gap-1" onClick={onClick}>
      <div className="text-xs tracking-wide text-grey-hide underline">
        {t('expandAllSizes')}
      </div>
      <ChevronIcon height="6" stroke="#A9A9A9" width="9" />
    </button>
  );
}
